import {
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Select,
  Input,
  Button,
  InputGroup,
  InputRightElement,
  Spinner,
  RadioGroup,
  Radio,
  HStack,
  Collapse,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';

import SectionCard from '../../components/SectionCard';
import PostalCodeInput from '../../components/PostalCodeInput';
import { useAnswerCodes, useValidationSchema } from '../../hooks';
import { isValidPostalCode } from '../../utils/postalCode';
import type { VehicleGaragingProps } from './types';

const VehicleGaraging = ({
  vehicle,
  index,
  onVehicleChange,
  postalCodeLookupIndex,
  availableCities,
  onPostalCodeChange,
  onOpenMap,
  contactProvinceMismatch = false,
}: VehicleGaragingProps) => {
  const { getOptions, getCode } = useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();
  const parkingOptions = getOptions('garaging');

  const vehicleEntity = vehicle as unknown as Record<string, unknown>;
  const sameAsApplicant = vehicle.garaging.same_as_applicant ?? false;
  const showProvinceMismatchWarning =
    contactProvinceMismatch && sameAsApplicant;
  const isParkingRequired = isFieldRequired(
    'vehicle',
    'garaging.parking_at_night',
    vehicleEntity,
  );
  const isPostalCodeRequired = isFieldRequired(
    'vehicle',
    'garaging.postal_code',
    vehicleEntity,
  );

  return (
    <SectionCard title="Garaging Location" mb={4}>
      <FormControl mb={4}>
        <FormLabel fontSize="sm">Same as Applicant Address?</FormLabel>
        <RadioGroup
          value={sameAsApplicant ? 'yes' : 'no'}
          onChange={(val) =>
            onVehicleChange(
              index,
              'garaging',
              val === 'yes',
              'same_as_applicant',
            )
          }
          isDisabled={false}
        >
          <HStack spacing={4}>
            <Radio value="no">No</Radio>
            <Radio value="yes">Yes</Radio>
          </HStack>
        </RadioGroup>
        {showProvinceMismatchWarning && (
          <Alert status="warning" mt={2} fontSize="sm">
            <AlertIcon />
            <AlertDescription>
              Contact is in a different province than the quote. Please enter
              the garaging address manually.
            </AlertDescription>
          </Alert>
        )}
      </FormControl>
      <FormControl
        isRequired={isParkingRequired}
        isInvalid={isParkingRequired && !vehicle.garaging.parking_at_night}
        mb={4}
      >
        <FormLabel fontSize="sm">Parking at Night</FormLabel>
        <Select
          variant="main"
          size="sm"
          value={getCode(vehicle.garaging.parking_at_night, 'garaging')}
          onChange={(e) =>
            onVehicleChange(
              index,
              'garaging',
              e.target.value,
              'parking_at_night',
            )
          }
          isDisabled={false}
        >
          {parkingOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FormControl>
      <Collapse
        in={!sameAsApplicant || showProvinceMismatchWarning}
        animateOpacity
      >
        <Grid
          templateColumns={{
            base: '1fr',
            sm: 'repeat(2, 1fr)',
          }}
          gap={4}
        >
          <GridItem>
            <PostalCodeInput
              value={vehicle.garaging.postal_code}
              onChange={(value) =>
                onVehicleChange(index, 'garaging', value, 'postal_code')
              }
              isDisabled={false}
              isRequired={isPostalCodeRequired}
              onBlur={() => {
                onPostalCodeChange(index, vehicle.garaging.postal_code);
              }}
              placeholder={
                postalCodeLookupIndex === index ? 'Looking up...' : undefined
              }
            />
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">City</FormLabel>
              {availableCities.length > 1 ? (
                <Select
                  variant="main"
                  size="sm"
                  value={vehicle.garaging.city}
                  onChange={(e) =>
                    onVehicleChange(index, 'garaging', e.target.value, 'city')
                  }
                  isDisabled={false}
                >
                  {availableCities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </Select>
              ) : (
                <InputGroup size="sm">
                  <Input
                    variant="main"
                    value={vehicle.garaging.city}
                    isReadOnly
                    placeholder={
                      postalCodeLookupIndex === index ? 'Loading...' : ''
                    }
                    pr={postalCodeLookupIndex === index ? '2.5rem' : undefined}
                  />
                  {postalCodeLookupIndex === index && (
                    <InputRightElement height="100%">
                      <Spinner size="sm" color="brand.500" />
                    </InputRightElement>
                  )}
                </InputGroup>
              )}
            </FormControl>
          </GridItem>
        </Grid>
        <HStack spacing={3} mt={3}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onOpenMap(index)}
            isDisabled={!isValidPostalCode(vehicle.garaging.postal_code)}
          >
            View Map
          </Button>
        </HStack>
      </Collapse>
    </SectionCard>
  );
};

export default VehicleGaraging;
